// safety/incidents-and-injuries/components/IncidentRegister.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Group, Download, Plus, ChevronDown, ChevronLeft, ChevronRight, TriangleAlert, Settings, Loader2, X, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { StatusPill } from './shared';
import { fmtDate, capacityColor, initials, buildIncidentsCsv, downloadCsv, STATUS_TRANSITIONS } from '../utils/incidents-utils';
import type { Incident, IncidentStatus, SafetyLists, EntityRef } from '../../../../types/incident.types';

const FILTERS: ('All' | IncidentStatus)[] = ['All', 'Open', 'In Progress', 'Under Investigation', 'Closed'];

const COLS: { label: string; key: string }[] = [
    { label: 'Injury #', key: 'incidentNumber' },
    { label: 'Worker Name', key: 'employeeName' },
    { label: 'Employer Entity', key: 'entityName' },
    { label: 'Project', key: 'project' },
    { label: 'Supervisor', key: 'supervisor' },
    { label: 'Date', key: 'date' },
    { label: 'Time', key: 'time' },
    { label: 'Status', key: 'status' },
    { label: 'Capacity', key: 'capacity' },
    { label: 'Next Appt', key: 'nextAppt' },
    { label: 'RTW Coordinator', key: 'rtwCoordinator' },
    { label: 'Last Updated', key: 'updatedAt' },
];

type GroupBy = '' | 'status' | 'entityName' | 'project' | 'capacity';
const GROUPS: { value: GroupBy; label: string }[] = [
    { value: '', label: 'No grouping' },
    { value: 'status', label: 'Status' },
    { value: 'entityName', label: 'Employer Entity' },
    { value: 'project', label: 'Project' },
    { value: 'capacity', label: 'Capacity' },
];

export interface RegisterFilters {
    capacity: string;
    entity: string;
    dateFrom: string;
    dateTo: string;
}
const EMPTY_FILTERS: RegisterFilters = { capacity: '', entity: '', dateFrom: '', dateTo: '' };

export interface RegisterSort {
    by: string;
    dir: 'asc' | 'desc';
}

const STATUSES: IncidentStatus[] = ['Open', 'In Progress', 'Under Investigation', 'Closed'];

export const IncidentRegister: React.FC<{
    incidents: Incident[];
    total: number;
    page: number;
    limit: number;
    loading: boolean;
    filter: 'All' | IncidentStatus;
    search: string;
    filters: RegisterFilters;
    sort: RegisterSort;
    stats: { open: number; expiringCertificates: number };
    lists: SafetyLists;
    entities: EntityRef[];
    onFilter: (f: 'All' | IncidentStatus) => void;
    onSearch: (s: string) => void;
    onFiltersChange: (f: RegisterFilters) => void;
    onSort: (by: string) => void;
    onPage: (p: number) => void;
    onOpen: (id: number) => void;
    onNew?: () => void;
    onExport: () => void;
    onBulkStatus: (ids: number[], status: IncidentStatus) => void;
    onSettings?: () => void;
}> = ({
    incidents, total, page, limit, loading, filter, search, filters, sort, stats, lists, entities,
    onFilter, onSearch, onFiltersChange, onSort, onPage, onOpen, onNew, onExport, onBulkStatus, onSettings,
}) => {
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [filterOpen, setFilterOpen] = useState(false);
    const [draftFilters, setDraftFilters] = useState<RegisterFilters>(filters);
    const [groupOpen, setGroupOpen] = useState(false);
    const [groupBy, setGroupBy] = useState<GroupBy>('');
    // Pending bulk status target — non-null opens the confirmation dialog
    const [bulkStatus, setBulkStatus] = useState<IncidentStatus | null>(null);

    // Clear the selection whenever the page data changes (page/filter/sort change)
    useEffect(() => setSelected(new Set()), [incidents]);

    const activeFilterCount = [filters.capacity, filters.entity, filters.dateFrom, filters.dateTo].filter(Boolean).length;
    const allSelected = incidents.length > 0 && incidents.every((r) => selected.has(r.id));

    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(incidents.map((r) => r.id)));
    const toggleOne = (id: number) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const exportSelected = () => {
        const rows = incidents.filter((r) => selected.has(r.id));
        downloadCsv(`incident-register-selected-${new Date().toISOString().slice(0, 10)}.csv`, buildIncidentsCsv(rows));
        toast.success(`Exported ${rows.length} records`);
    };

    const confirmBulkStatus = () => {
        if (!bulkStatus) return;
        onBulkStatus([...selected], bulkStatus);
        setSelected(new Set());
        setBulkStatus(null);
    };

    const groupedRows = useMemo(() => {
        if (!groupBy) return null;
        const map = new Map<string, Incident[]>();
        for (const r of incidents) {
            const key = (r[groupBy] as string) || '—';
            const rows = map.get(key);
            if (rows) rows.push(r);
            else map.set(key, [r]);
        }
        return [...map.entries()];
    }, [incidents, groupBy]);

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, total);

    const renderRow = (r: Incident) => (
        <tr key={r.id} className="border-b border-border last:border-0 hover:bg-accent/40 text-sm">
            <td className="px-3 py-3">
                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} className="rounded border-border" />
            </td>
            <td className="px-3 py-3 whitespace-nowrap">
                <button onClick={() => onOpen(r.id)} className="text-primary font-medium hover:underline cursor-pointer">
                    {r.incidentNumber}
                </button>
            </td>
            <td className="px-3 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-muted text-muted-foreground text-[10px] font-medium flex items-center justify-center">
                        {initials(r.employeeName)}
                    </span>
                    {r.employeeName}
                </div>
            </td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.entityName}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.project || '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.supervisor || '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(r.date)}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.time || '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                    <StatusPill status={r.status} />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                title="Change Status"
                                aria-label="Change Status"
                                className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground"
                            >
                                <Repeat size={12} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {(STATUS_TRANSITIONS[r.status] ?? []).map((s) => (
                                <DropdownMenuItem key={s} onSelect={() => onBulkStatus([r.id], s)}>
                                    {s}
                                </DropdownMenuItem>
                            ))}
                            {(STATUS_TRANSITIONS[r.status] ?? []).length === 0 && (
                                <DropdownMenuItem disabled>No transitions available</DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </td>
            <td className={`px-3 py-3 whitespace-nowrap font-medium ${capacityColor(r.capacity)}`}>{r.capacity || '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.nextAppt ? fmtDate(r.nextAppt) : '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{r.rtwCoordinator || '–'}</td>
            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(r.updatedAt)}</td>
        </tr>
    );

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1500px] mx-auto w-full">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-medium">Incidents &amp; Injuries</h1>
                    <p className="text-sm text-muted-foreground mt-1">Australian WHS incident management and return-to-work tracking</p>
                </div>
                <div className="flex items-center gap-2">
                    {onSettings && (
                        <button onClick={onSettings} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                            <Settings size={16} />
                            Settings
                        </button>
                    )}
                    <button onClick={onExport} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                        <Download size={16} />
                        Export
                    </button>
                    {onNew && (
                        <button onClick={onNew} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                            <Plus size={16} />
                            New Incident
                        </button>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder="Search incidents..."
                            className="w-64 h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm"
                        />
                    </div>

                    {/* Filter popover */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                // Seed the draft from the applied filters each time the panel opens
                                if (!filterOpen) setDraftFilters(filters);
                                setFilterOpen((v) => !v);
                                setGroupOpen(false);
                            }}
                            className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border text-sm font-medium ${
                                activeFilterCount > 0 ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border bg-card hover:bg-accent'
                            }`}
                        >
                            <Filter size={16} />
                            Filter
                            {activeFilterCount > 0 && (
                                <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                        {filterOpen && (
                            <div className="absolute left-0 top-11 z-30 w-72 rounded-lg border border-border bg-card shadow-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold">Filters</span>
                                    <button onClick={() => setFilterOpen(false)} className="text-muted-foreground hover:text-foreground">
                                        <X size={16} />
                                    </button>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground">Capacity</label>
                                    <select
                                        value={draftFilters.capacity}
                                        onChange={(e) => setDraftFilters({ ...draftFilters, capacity: e.target.value })}
                                        className="w-full h-9 mt-1 rounded-lg border border-border bg-card px-2.5 text-sm"
                                    >
                                        <option value="">All</option>
                                        {lists.capacityStatus.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground">Employer Entity</label>
                                    <select
                                        value={draftFilters.entity}
                                        onChange={(e) => setDraftFilters({ ...draftFilters, entity: e.target.value })}
                                        className="w-full h-9 mt-1 rounded-lg border border-border bg-card px-2.5 text-sm"
                                    >
                                        <option value="">All</option>
                                        {entities.map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground">Date from</label>
                                        <input
                                            type="date"
                                            value={draftFilters.dateFrom}
                                            onChange={(e) => setDraftFilters({ ...draftFilters, dateFrom: e.target.value })}
                                            className="w-full h-9 mt-1 rounded-lg border border-border bg-card px-2.5 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground">Date to</label>
                                        <input
                                            type="date"
                                            value={draftFilters.dateTo}
                                            onChange={(e) => setDraftFilters({ ...draftFilters, dateTo: e.target.value })}
                                            className="w-full h-9 mt-1 rounded-lg border border-border bg-card px-2.5 text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-1">
                                    <button
                                        onClick={() => { onFiltersChange(EMPTY_FILTERS); setFilterOpen(false); }}
                                        className="h-9 px-3.5 rounded-lg border border-border text-sm font-medium hover:bg-accent"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        onClick={() => { onFiltersChange(draftFilters); setFilterOpen(false); }}
                                        className="h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Group popover */}
                    <div className="relative">
                        <button
                            onClick={() => { setGroupOpen((v) => !v); setFilterOpen(false); }}
                            className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border text-sm font-medium ${
                                groupBy ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border bg-card hover:bg-accent'
                            }`}
                        >
                            <Group size={16} />
                            {groupBy ? `Group: ${GROUPS.find((g) => g.value === groupBy)?.label}` : 'Group'}
                        </button>
                        {groupOpen && (
                            <div className="absolute left-0 top-11 z-30 w-52 rounded-lg border border-border bg-card shadow-lg p-1.5">
                                {GROUPS.map((g) => (
                                    <button
                                        key={g.value}
                                        onClick={() => { setGroupBy(g.value); setGroupOpen(false); }}
                                        className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                                            groupBy === g.value ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent'
                                        }`}
                                    >
                                        {g.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-1 ml-1">
                        {FILTERS.map((f) => (
                            <button
                                key={f}
                                onClick={() => onFilter(f)}
                                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium ${
                                    filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                {loading && <Loader2 size={18} className="animate-spin text-muted-foreground" />}
            </div>

            {/* Bulk action bar */}
            {selected.size > 0 && (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-accent/40 px-4 py-2.5 mb-4 flex-wrap">
                    <span className="text-sm font-medium">{selected.size} selected</span>
                    <button
                        onClick={exportSelected}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border bg-card text-xs font-medium hover:bg-accent"
                    >
                        <Download size={14} />
                        Export Selected
                    </button>
                    <select
                        value=""
                        onChange={(e) => {
                            const st = e.target.value as IncidentStatus | '';
                            if (st) setBulkStatus(st);
                        }}
                        className="h-8 rounded-lg border border-border bg-card px-2 text-xs font-medium"
                    >
                        <option value="">Change status…</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => setSelected(new Set())} className="text-xs font-medium text-muted-foreground hover:text-foreground ml-auto">
                        Clear selection
                    </button>
                </div>
            )}

            {(stats.open > 0 || stats.expiringCertificates > 0) && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-4">
                    <TriangleAlert size={16} className="text-destructive flex-shrink-0" />
                    <p className="text-sm text-foreground">
                        {stats.open > 0 && (
                            <>
                                <span className="font-medium text-destructive">{stats.open} open {stats.open === 1 ? 'injury' : 'injuries'}</span>
                                {stats.open === 1 ? ' requires' : ' require'} attention.
                            </>
                        )}
                        {stats.expiringCertificates > 0 && (
                            <> {stats.expiringCertificates} {stats.expiringCertificates === 1 ? 'certificate' : 'certificates'} expiring within 4 days.</>
                        )}
                    </p>
                </div>
            )}

            <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="border-b border-border bg-muted/30">
                            <tr>
                                <th className="px-3 py-3 w-10">
                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-border" />
                                </th>
                                {COLS.map((c) => (
                                    <th
                                        key={c.key}
                                        onClick={() => onSort(c.key)}
                                        className="text-left font-medium text-xs uppercase tracking-wide text-muted-foreground px-3 py-3 whitespace-nowrap cursor-pointer select-none hover:text-foreground"
                                    >
                                        {c.label}{' '}
                                        <ChevronDown
                                            size={12}
                                            className={`inline transition-transform ${
                                                sort.by === c.key ? 'opacity-100 text-foreground' : 'opacity-40'
                                            } ${sort.by === c.key && sort.dir === 'asc' ? 'rotate-180' : ''}`}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {incidents.length === 0 && (
                                <tr>
                                    <td colSpan={COLS.length + 1} className="px-3 py-16 text-center text-sm text-muted-foreground">
                                        {loading ? 'Loading incidents…' : 'No incidents match your filters.'}
                                    </td>
                                </tr>
                            )}
                            {groupedRows
                                ? groupedRows.map(([key, rows]) => (
                                    <React.Fragment key={key}>
                                        <tr className="bg-muted/40 border-b border-border">
                                            <td colSpan={COLS.length + 1} className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                                {GROUPS.find((g) => g.value === groupBy)?.label}: {key} <span className="font-normal">({rows.length})</span>
                                            </td>
                                        </tr>
                                        {rows.map(renderRow)}
                                    </React.Fragment>
                                ))
                                : incidents.map(renderRow)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                <p className="text-sm text-muted-foreground">
                    Showing <span className="font-medium text-foreground">{from}</span>–<span className="font-medium text-foreground">{to}</span> of{' '}
                    <span className="font-medium text-foreground">{total}</span> records
                </p>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPage(page - 1)}
                        disabled={page <= 1}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft size={16} />
                        Previous
                    </button>
                    <span className="text-sm font-medium px-2">{page} / {totalPages}</span>
                    <button
                        onClick={() => onPage(page + 1)}
                        disabled={page >= totalPages}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {/* Bulk status change confirmation dialog */}
            <AlertDialog open={bulkStatus !== null} onOpenChange={(open) => { if (!open) setBulkStatus(null); }}>
                <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <Repeat size={20} className="text-primary" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Change Incident Status</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Change status of <span className="font-bold text-gray-700">{selected.size}</span>{' '}
                            {selected.size === 1 ? 'incident' : 'incidents'} to{' '}
                            <span className="font-bold text-gray-700">{bulkStatus}</span>?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5">
                            Cancel
                        </AlertDialogCancel>
                        <button
                            onClick={confirmBulkStatus}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 rounded-xl text-sm font-bold px-5"
                        >
                            Change Status
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
